// SEQ-193: the extra fetch a month-on-month table needs in "group by
// project" mode.
//
// WHY THIS IS ITS OWN HOOK, OUTSIDE THE SHARED REQUEST PLAN. Every other
// widget's data comes from `usePnlDashboardData`/`buildPnlRequestPlan`
// (dashboardRequestKeys.ts), which groups widgets into at most
// MAX_REQUEST_KEYS RPC calls, ONE KEY PER WIDGET (D20). The RPC itself has no
// grain that returns a project broken down by month in a single call (see
// rollup.ts's own file header: "no returned row ever carries both a
// projectId and a periodMonth"), so getting several projects' month-by-month
// figures needs one call PER PROJECT. Widening the shared plan to let one
// widget claim several keys would touch `PnlRequestPlan`'s
// one-key-per-widget contract that `usePnlDashboardData`, `widgetDataset.ts`
// and `PnlWidgetRenderer` all rely on, for a need exactly one chart type has.
// This hook instead calls `callPnlAnalytics` directly, bounded to
// MONTH_ON_MONTH_MAX_PROJECTS calls, exactly like the existing monthly trend
// widget's own "one selected project" on-demand call
// (usePnlAnalyticsForProject in usePnlAnalytics.ts) already does for one
// project at a time.
//
// The 350ms debounce and cancel-on-unmount discipline match
// usePnlDashboardData.ts so a fast filter change does not race two sets of
// calls against each other.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { callPnlAnalytics } from "../../lib/pnl/analyticsClient";
import type { PnlAnalyticsRow } from "../../lib/pnl/analyticsClient";
import { MONTH_ON_MONTH_MAX_PROJECTS } from "./monthOnMonth";
import { EMPTY_FETCHED, viewForRequest, type MonthOnMonthFetched } from "./monthOnMonthProjectState";

const DEBOUNCE_MS = 350;

export interface MonthOnMonthProjectRowsParams {
  /** False skips the fetch entirely (e.g. the widget is in "company" mode, or is still being edited into shape). */
  enabled: boolean;
  /** 1 to MONTH_ON_MONTH_MAX_PROJECTS project ids. More than the cap is treated as "too many" by the caller, not fetched here. */
  projectIds: string[];
  metricKey: string;
  from: string | null;
  to: string | null;
  statuses: string[];
}

export interface MonthOnMonthProjectRowsResult {
  /** One project's month-grain rows, keyed by project id. A project missing from this map has not loaded yet (or its call failed). */
  rowsByProject: Record<string, PnlAnalyticsRow[]>;
  loading: boolean;
  /** One message per failed project id, so one project's RLS/network hiccup does not blank a table whose other rows loaded fine. */
  errorByProject: Record<string, string>;
}

export function useMonthOnMonthProjectRows(params: MonthOnMonthProjectRowsParams): MonthOnMonthProjectRowsResult {
  const { enabled, projectIds, metricKey, from, to, statuses } = params;
  const [fetched, setFetched] = useState<MonthOnMonthFetched>(EMPTY_FETCHED);

  // Bounded, deduplicated, and order-independent, so two renders that name
  // the same projects in a different order do not refetch.
  const boundedIds = useMemo(() => {
    const unique = [...new Set(projectIds)];
    return unique.slice(0, MONTH_ON_MONTH_MAX_PROJECTS);
  }, [projectIds]);

  const requestKey = useMemo(
    () => JSON.stringify({ ids: [...boundedIds].sort(), metricKey, from, to, statuses: [...statuses].sort() }),
    [boundedIds, metricKey, from, to, statuses],
  );

  const active = enabled && boundedIds.length > 0 && from !== null && to !== null;

  useEffect(() => {
    if (!active || from === null || to === null) {
      setFetched(EMPTY_FETCHED);
      return;
    }

    let cancelled = false;
    const handle = setTimeout(() => {
      Promise.all(
        boundedIds.map(async (projectId) => {
          try {
            const response = await callPnlAnalytics(supabase, {
              from,
              to,
              projectIds: [projectId],
              statuses,
              groupBy: "month",
              metrics: [metricKey],
              schemaVersion: 1,
            });
            return { projectId, rows: response.rows, error: null as string | null };
          } catch (err: unknown) {
            return { projectId, rows: [] as PnlAnalyticsRow[], error: err instanceof Error ? err.message : String(err) };
          }
        }),
      ).then((results) => {
        if (cancelled) return;
        const nextRows: Record<string, PnlAnalyticsRow[]> = {};
        const nextErrors: Record<string, string> = {};
        for (const result of results) {
          if (result.error) nextErrors[result.projectId] = result.error;
          else nextRows[result.projectId] = result.rows;
        }
        setFetched({ key: requestKey, rowsByProject: nextRows, errorByProject: nextErrors });
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- setTimeout debounce window, same as usePnlDashboardData.ts
    }, DEBOUNCE_MS) as any;

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- requestKey is the real dependency
  }, [requestKey, enabled]);

  // Derived on every render, so a changed input reports "loading" and hides
  // the previous inputs' figures in the same render, not after an effect.
  return viewForRequest(fetched, requestKey, active);
}
